import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './AdminDashboard.css';
import UserManagement from './UserManagement';
import DepositManagement from './DepositManagement';
import WithdrawalManagement from './WithdrawalManagement';
import OrderManagement from './OrderManagement';
import KYCPending from './KYCPending';
import WalletAddressManagement from './WalletAddressManagement';
import WalletManagement from './WalletManagement'; // New import
import AdminChat from './AdminChat';

function AdminDashboard({ socket }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [chatNotice, setChatNotice] = useState(null);
  const [pendingCounts, setPendingCounts] = useState({
    orders: 0,
    deposits: 0,
    withdrawals: 0,
    kyc: 0,
    frozenWallets: 0,
    chats: 0
  });

  // Auto-refresh interval in seconds
  const refreshInterval = 5;

  // Fetch pending counts separately
  const fetchPendingCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all pending counts in parallel with better error handling
      const endpoints = [
        'https://trcsite.net/api/admin/orders/pending/count',
        'https://trcsite.net/api/admin/deposits/pending/count',
        'https://trcsite.net/api/admin/withdrawals/pending/count',
        'https://trcsite.net/api/admin/kyc/pending/count',
        'https://trcsite.net/api/admin/wallets/frozen/count',
        'https://trcsite.net/api/chat/admin/unread-count'
      ];

      const requests = endpoints.map(endpoint => 
        axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(error => {
          console.warn(`Failed to fetch from ${endpoint}:`, error.message);
          return { data: { count: 0 } };
        })
      );

      const [ordersResponse, depositsResponse, withdrawalsResponse, kycResponse, frozenResponse, chatResponse] = await Promise.all(requests);

      setPendingCounts({
        orders: ordersResponse.data?.count || 0,
        deposits: depositsResponse.data?.count || 0,
        withdrawals: withdrawalsResponse.data?.count || 0,
        kyc: kycResponse.data?.count || 0,
        frozenWallets: frozenResponse.data?.count || 0,
        chats: chatResponse.data?.count || 0
      });

    } catch (error) {
      console.error('Error fetching pending counts:', error);
      // Fallback to stats data if available
      setPendingCounts({
        orders: stats.pending_orders || 0,
        deposits: stats.pending_deposits || 0,
        withdrawals: stats.pending_withdrawals_count || 0,
        kyc: stats.pending_kyc || 0,
        frozenWallets: stats.frozen_wallets || 0,
        chats: 0
      });
    }
  };

  const fetchDashboardStats = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get('https://trcsite.net/api/admin/dashboard');
      const data = response.data;
      
      setStats(data);
      setLastUpdate(Date.now());
      
      // Update pending counts from dashboard data as fallback
      setPendingCounts(prev => ({
        orders: data.pending_orders || prev.orders,
        deposits: data.pending_deposits || prev.deposits,
        withdrawals: data.pending_withdrawals_count || prev.withdrawals,
        kyc: data.pending_kyc || prev.kyc,
        frozenWallets: data.frozen_wallets || prev.frozenWallets,
        chats: prev.chats
      }));
      
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchDashboardStats();
    fetchPendingCounts();
  }, []);

  useEffect(() => {
    if (!socket) return;

    const joinAdmin = () => socket.emit('joinAdminRoom');
    joinAdmin();
    socket.on('connect', joinAdmin);
    socket.on('authenticated', joinAdmin);

    const handleChat = (message) => {
      if (!message || message.sender_role !== 'user') return;
      const preview = message.message_type === 'image'
        ? 'Sent an image'
        : message.message_type === 'file'
          ? 'Sent a file'
          : (message.message || 'New support message');
      const name = message.sender_name || message.room?.user_name || 'Customer';
      setChatNotice(`${name}: ${preview}`);
      setPendingCounts((prev) => ({ ...prev, chats: (prev.chats || 0) + 1 }));
    };

    socket.on('adminChatMessage', handleChat);

    return () => {
      socket.off('connect', joinAdmin);
      socket.off('authenticated', joinAdmin);
      socket.off('adminChatMessage', handleChat);
    };
  }, [socket]);

  useEffect(() => {
    if (!chatNotice) return undefined;
    const timer = setTimeout(() => setChatNotice(null), 6000);
    return () => clearTimeout(timer);
  }, [chatNotice]);

  // Set up interval for active tab
  useEffect(() => {
    if (activeTab === 'dashboard') {
      const interval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          fetchDashboardStats();
          fetchPendingCounts();
        }
      }, refreshInterval * 1000);

      return () => clearInterval(interval);
    }
  }, [activeTab]);

  // Refresh pending counts when switching tabs
  useEffect(() => {
    if (activeTab !== 'dashboard') {
      fetchPendingCounts();
    }
  }, [activeTab]);

  const handleRefresh = () => {
    if (activeTab === 'dashboard') {
      fetchDashboardStats();
    }
    fetchPendingCounts();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <DashboardContent 
            stats={stats} 
            loading={loading} 
            refreshing={refreshing}
            onRefresh={handleRefresh}
            lastUpdate={lastUpdate}
            refreshInterval={refreshInterval}
            pendingCounts={pendingCounts}
          />
        );
      case 'wallet-management': // New tab
        return <WalletManagement />;
      case 'wallet-addresses':
        return <WalletAddressManagement />;
      case 'users':
        return <UserManagement />;
      case 'deposits':
        return <DepositManagement />;
      case 'withdrawals':
        return <WithdrawalManagement />;
      case 'orders':
        return <OrderManagement />;
      case 'kyc':
        return <KYCPending />;
      case 'chats':
        return <AdminChat socket={socket} />;
      default:
        return (
          <DashboardContent 
            stats={stats} 
            loading={loading} 
            refreshing={refreshing}
            onRefresh={handleRefresh}
            lastUpdate={lastUpdate}
            refreshInterval={refreshInterval}
            pendingCounts={pendingCounts}
          />
        );
    }
  };

  if (loading && activeTab === 'dashboard') {
    return <div className="loading">Loading admin dashboard...</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-text">
            <h1>Admin Dashboard</h1>
            <p>Platform overview and management</p>
          </div>
          
          {activeTab === 'dashboard' && (
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`global-refresh-btn ${refreshing ? 'refreshing' : ''}`}
              title={`Auto-refreshes every ${refreshInterval} seconds`}
            >
              <span className="refresh-icon">
                {refreshing ? '⏳' : '🔄'}
              </span>
              <span className="refresh-text">
                {refreshing ? 'Refreshing...' : 'Refresh Dashboard'}
              </span>
              <span className="refresh-interval">({refreshInterval}s)</span>
            </button>
          )}
        </div>
      </div>

      {/* Navigation Tabs with Badges */}
      <div className="admin-nav">
        <button
          onClick={() => handleTabChange('dashboard')}
          className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
        >
          📊 Dashboard
        </button>

        <button
          onClick={() => handleTabChange('wallet-management')} // New tab
          className={`nav-tab ${activeTab === 'wallet-management' ? 'active' : ''}`}
        >
          🛄 Wallet Management
          {pendingCounts.frozenWallets > 0 && (
            <span className={`nav-badge frozen-badge ${pendingCounts.frozenWallets > 5 ? 'urgent' : ''}`}>
              {pendingCounts.frozenWallets > 99 ? '99+' : pendingCounts.frozenWallets}
            </span>
          )}
        </button>

        <button
          onClick={() => handleTabChange('wallet-addresses')}
          className={`nav-tab ${activeTab === 'wallet-addresses' ? 'active' : ''}`}
        >
          🔑 Wallet Addresses
        </button>
        
        <button
          onClick={() => handleTabChange('users')}
          className={`nav-tab ${activeTab === 'users' ? 'active' : ''}`}
        >
          👥 Users
        </button>
        
        <button
          onClick={() => handleTabChange('orders')}
          className={`nav-tab ${activeTab === 'orders' ? 'active' : ''}`}
        >
          📝 Orders
          {pendingCounts.orders > 0 && (
            <span className={`nav-badge order-badge ${pendingCounts.orders > 10 ? 'urgent' : ''}`}>
              {pendingCounts.orders > 99 ? '99+' : pendingCounts.orders}
            </span>
          )}
        </button>
        
        <button
          onClick={() => handleTabChange('deposits')}
          className={`nav-tab ${activeTab === 'deposits' ? 'active' : ''}`}
        >
          💰 Deposits
          {pendingCounts.deposits > 0 && (
            <span className={`nav-badge deposit-badge ${pendingCounts.deposits > 10 ? 'urgent' : ''}`}>
              {pendingCounts.deposits > 99 ? '99+' : pendingCounts.deposits}
            </span>
          )}
        </button>
        
        <button
          onClick={() => handleTabChange('withdrawals')}
          className={`nav-tab ${activeTab === 'withdrawals' ? 'active' : ''}`}
        >
          🏦 Withdrawals
          {pendingCounts.withdrawals > 0 && (
            <span className={`nav-badge withdrawal-badge ${pendingCounts.withdrawals > 10 ? 'urgent' : ''}`}>
              {pendingCounts.withdrawals > 99 ? '99+' : pendingCounts.withdrawals}
            </span>
          )}
        </button>
        
        <button
          onClick={() => handleTabChange('kyc')}
          className={`nav-tab ${activeTab === 'kyc' ? 'active' : ''}`}
        >
          ✅ KYC Verification
          {pendingCounts.kyc > 0 && (
            <span className={`nav-badge kyc-badge ${pendingCounts.kyc > 10 ? 'urgent' : ''}`}>
              {pendingCounts.kyc > 99 ? '99+' : pendingCounts.kyc}
            </span>
          )}
        </button>

        <button
          onClick={() => handleTabChange('chats')}
          className={`nav-tab ${activeTab === 'chats' ? 'active' : ''}`}
        >
          💬 Support Chat
          {pendingCounts.chats > 0 && (
            <span className={`nav-badge chat-badge ${pendingCounts.chats > 5 ? 'urgent' : ''}`}>
              {pendingCounts.chats > 99 ? '99+' : pendingCounts.chats}
            </span>
          )}
        </button>
      </div>

      {chatNotice && activeTab !== 'chats' && (
        <div className="admin-chat-toast" onClick={() => handleTabChange('chats')}>
          <strong>New customer message</strong>
          <span>{chatNotice}</span>
          <em>Open chat</em>
        </div>
      )}

      {renderTabContent()}
    </div>
  );
}

function DashboardContent({ stats, loading, refreshing, onRefresh, lastUpdate, refreshInterval, pendingCounts }) {
  const chartData = stats.daily_registrations?.map(day => ({
    date: new Date(day.date).toLocaleDateString(),
    registrations: day.daily_registrations
  })).reverse() || [];

  const getTimeSinceUpdate = () => {
    const seconds = Math.floor((Date.now() - lastUpdate) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    return `${Math.floor(seconds / 60)}m ago`;
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner-large"></div>
        <div className="loading-text">Loading dashboard data...</div>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-controls">
        <div className="update-info">
          <span className="last-update">Last update: {getTimeSinceUpdate()}</span>
          <span className="auto-refresh">Auto-refresh: {refreshInterval}s</span>
        </div>
        
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
        >
          <span className="refresh-icon">
            {refreshing ? '⏳' : '🔄'}
          </span>
          <span className="refresh-text">
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </span>
        </button>
      </div>

      {/* Pending Actions Overview */}
      {(pendingCounts.orders > 0 || pendingCounts.deposits > 0 || pendingCounts.withdrawals > 0 || pendingCounts.kyc > 0 || pendingCounts.frozenWallets > 0) && (
        <div className="pending-overview">
          <h3>🔄 Pending Actions Requiring Attention</h3>
          <div className="pending-grid">
            {pendingCounts.orders > 0 && (
              <div className="pending-item order-pending" onClick={() => window.location.hash = '#orders'}>
                <div className="pending-icon">📝</div>
                <div className="pending-content">
                  <div className="pending-label">Pending Orders</div>
                  <div className="pending-count">{pendingCounts.orders}</div>
                </div>
                <div className="pending-arrow">→</div>
              </div>
            )}
            
            {pendingCounts.deposits > 0 && (
              <div className="pending-item deposit-pending" onClick={() => window.location.hash = '#deposits'}>
                <div className="pending-icon">💰</div>
                <div className="pending-content">
                  <div className="pending-label">Pending Deposits</div>
                  <div className="pending-count">{pendingCounts.deposits}</div>
                </div>
                <div className="pending-arrow">→</div>
              </div>
            )}
            
            {pendingCounts.withdrawals > 0 && (
              <div className="pending-item withdrawal-pending" onClick={() => window.location.hash = '#withdrawals'}>
                <div className="pending-icon">🏦</div>
                <div className="pending-content">
                  <div className="pending-label">Pending Withdrawals</div>
                  <div className="pending-count">{pendingCounts.withdrawals}</div>
                </div>
                <div className="pending-arrow">→</div>
              </div>
            )}
            
            {pendingCounts.kyc > 0 && (
              <div className="pending-item kyc-pending" onClick={() => window.location.hash = '#kyc'}>
                <div className="pending-icon">✅</div>
                <div className="pending-content">
                  <div className="pending-label">Pending KYC</div>
                  <div className="pending-count">{pendingCounts.kyc}</div>
                </div>
                <div className="pending-arrow">→</div>
              </div>
            )}

            {pendingCounts.frozenWallets > 0 && (
              <div className="pending-item frozen-pending" onClick={() => window.location.hash = '#wallet-management'}>
                <div className="pending-icon">🛄</div>
                <div className="pending-content">
                  <div className="pending-label">Frozen Wallets</div>
                  <div className="pending-count">{pendingCounts.frozenWallets}</div>
                </div>
                <div className="pending-arrow">→</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="market-grid">
        <div className="market-card stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-label">Total Users</div>
            <div className="stat-value">{stats.total_users || 0}</div>
          </div>
        </div>
        
        <div className="market-card stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-label">Verified Users</div>
            <div className="stat-value">{stats.verified_users || 0}</div>
          </div>
        </div>
        
        <div className="market-card stat-card">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <div className="stat-label">KYC Pending</div>
            <div className="stat-value highlight-warning">{stats.kyc_pending || 0}</div>
            {pendingCounts.kyc > 0 && (
              <div className="stat-badge">{pendingCounts.kyc} new</div>
            )}
          </div>
        </div>
        
        <div className="market-card stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-label">KYC Approved</div>
            <div className="stat-value highlight-success">{stats.kyc_approved || 0}</div>
          </div>
        </div>

        <div className="market-card stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <div className="stat-label">Active Orders</div>
            <div className="stat-value">{stats.active_orders || 0}</div>
            {pendingCounts.orders > 0 && (
              <div className="stat-badge">{pendingCounts.orders} pending</div>
            )}
          </div>
        </div>

        <div className="market-card stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <div className="stat-label">Total Deposits</div>
            <div className="stat-value">{stats.total_deposits || 0}</div>
            {pendingCounts.deposits > 0 && (
              <div className="stat-badge">{pendingCounts.deposits} pending</div>
            )}
          </div>
        </div>

        <div className="market-card stat-card">
          <div className="stat-icon">🏦</div>
          <div className="stat-content">
            <div className="stat-label">Pending Withdrawals</div>
            <div className="stat-value highlight-warning">{stats.pending_withdrawals || 0}</div>
            {pendingCounts.withdrawals > 0 && (
              <div className="stat-badge">{pendingCounts.withdrawals} new</div>
            )}
          </div>
        </div>

        <div className="market-card stat-card">
          <div className="stat-icon">🛄</div>
          <div className="stat-content">
            <div className="stat-label">Frozen Wallets</div>
            <div className="stat-value highlight-danger">{stats.frozen_wallets || 0}</div>
            {pendingCounts.frozenWallets > 0 && (
              <div className="stat-badge">{pendingCounts.frozenWallets} active</div>
            )}
          </div>
        </div>
      </div>

      {/* Registration Chart */}
      <div className="chart-section">
        <div className="chart-header">
          <h3>Daily Registrations</h3>
          <div className="chart-controls">
            <span className="chart-update">Updated {getTimeSinceUpdate()}</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#344258" />
            <XAxis dataKey="date" stroke="#b0b8c4" />
            <YAxis stroke="#b0b8c4" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#1e2a3a', 
                borderColor: '#344258',
                borderRadius: '8px',
                color: '#ffffff'
              }}
              itemStyle={{ color: '#ffffff' }}
            />
            <Legend />
            <Bar 
              dataKey="registrations" 
              fill="#2962ff" 
              name="Registrations"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Recent Activity */}
      <div className="trading-layout">
        <div className="chart-section">
          <div className="chart-header">
            <h3>Recent Orders</h3>
            <div className="header-right">
              <span className="record-count">{stats.recent_orders?.length || 0} orders</span>
              {pendingCounts.orders > 0 && (
                <span className="pending-alert">{pendingCounts.orders} Trading</span>
              )}
            </div>
          </div>
          {stats.recent_orders?.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Pair</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_orders.map((order, index) => (
                    <tr key={index}>
                      <td>
                        <div className="user-cell">
                          <span className="user-name">{order.user_name}</span>
                          <span className="user-email">{order.user_email}</span>
                        </div>
                      </td>
                      <td className="pair-cell">{order.pair}</td>
                      <td>
                        <span className={`order-type ${order.type}`}>
                          {order.type.toUpperCase()}
                        </span>
                      </td>
                      <td className="amount-cell">{order.amount}</td>
                      <td>
                        <span className={`status-badge status-${order.status}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="time-cell">
                        {new Date(order.created_at).toLocaleTimeString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">
              <span className="no-data-icon">📝</span>
              <span>No recent orders</span>
            </div>
          )}
        </div>

        <div className="chart-section">
          <div className="chart-header">
            <h3>Recent Trades</h3>
            <span className="record-count">{stats.recent_trades?.length || 0} trades</span>
          </div>
          {stats.recent_trades?.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Pair</th>
                    <th>Type</th>
                    <th>Price</th>
                    <th>Amount</th>
                    <th>Total</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_trades.map((trade, index) => (
                    <tr key={index}>
                      <td className="pair-cell">{trade.pair}</td>
                      <td>
                        <span className={`trade-type ${trade.type}`}>
                          {trade.type.toUpperCase()}
                        </span>
                      </td>
                      <td className="price-cell">${trade.price}</td>
                      <td className="amount-cell">{trade.amount}</td>
                      <td className="total-cell">
                        ${(parseFloat(trade.price) * parseFloat(trade.amount)).toFixed(2)}
                      </td>
                      <td className="time-cell">
                        {new Date(trade.executed_at).toLocaleTimeString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">
              <span className="no-data-icon">💸</span>
              <span>No recent trades</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default AdminDashboard;